interface MediaSessionLike {
  setActionHandler(
    action: MediaSessionAction,
    handler: MediaSessionActionHandler | null,
  ): void;
}

interface MediaSessionActions {
  play: () => void;
  pause: () => void;
  next: () => void;
  previous: () => void;
}

const ACTIONS: ReadonlyArray<
  readonly [MediaSessionAction, keyof MediaSessionActions]
> = [
  ["play", "play"],
  ["pause", "pause"],
  ["nexttrack", "next"],
  ["previoustrack", "previous"],
];

export function absoluteCoverUrl(trackId: number, origin: string): string {
  return new URL(`/api/v1/tracks/${trackId}/cover`, origin).href;
}

export function mediaSessionArtwork(coverAbsoluteUrl: string): MediaImage[] {
  return [96, 256, 512].map((size) => ({
    src: coverAbsoluteUrl,
    sizes: `${size}x${size}`,
    type: "image/jpeg",
  }));
}

/**
 * Keep Android/lock-screen media notifications in sync with the session.
 */
export function updateMediaSessionPlayback(
  session: Pick<MediaSession, "playbackState" | "setPositionState">,
  opts: { playing: boolean; durationSec: number | null; positionSec: number },
): void {
  session.playbackState = opts.playing ? "playing" : "paused";
  const duration = opts.durationSec;
  if (duration == null || !(duration > 0) || !Number.isFinite(opts.positionSec)) return;
  try {
    session.setPositionState({
      duration,
      playbackRate: 1,
      position: Math.max(0, Math.min(duration, opts.positionSec)),
    });
  } catch {
    // Browser/device does not support position state.
  }
}

/**
 * Track-end and overlapping advance must not POST /playback/pause via Media Session.
 */
export function shouldHonorMediaSessionPause(opts: {
  elementEnded: boolean;
  advancing: boolean;
}): boolean {
  return !opts.elementEnded && !opts.advancing;
}

/**
 * Connect Bluetooth, lock-screen, CarPlay, and Android Auto controls to the
 * same server-authoritative actions as the on-page player.
 */
export function installMediaSessionHandlers(
  session: MediaSessionLike,
  actions: MediaSessionActions,
): () => void {
  const installed: MediaSessionAction[] = [];
  for (const [mediaAction, playerAction] of ACTIONS) {
    try {
      session.setActionHandler(mediaAction, actions[playerAction]);
      installed.push(mediaAction);
    } catch {
      // Browser/device does not support this specific action.
    }
  }

  return () => {
    for (const action of installed) {
      try {
        session.setActionHandler(action, null);
      } catch {
        // Session disappeared or action support changed.
      }
    }
  };
}
